interface ICallbackFunction extends Function {
    selectTimeOut?: number;
    amountOfCall?: number;
}

export default class Extensions {
    static executeTimeout(callBack: Function, timeout: number, scope?: unknown, ...args: unknown[]) {
        const extendedFunction = <ICallbackFunction>callBack;
        if (extendedFunction.selectTimeOut) {
            clearTimeout(extendedFunction.selectTimeOut);
        }
        extendedFunction.selectTimeOut = window.setTimeout(() => {
            callBack.apply(scope, args);
        }, timeout);
    }

    static debounce<T extends (...args: unknown[]) => Promise<void> | void>(
        func: T,
        wait: number
    ): T & { cancel: () => void } {
        let timeoutId: ReturnType<typeof setTimeout> | null = null;
        let isRunning = false;
        let pendingArgs: Parameters<T> | null = null;

        const runFunc = async () => {
            if (pendingArgs === null) return;

            isRunning = true;
            const args = pendingArgs;
            pendingArgs = null;

            try {
                await func(...args);
            } finally {
                isRunning = false;
            }

            // If new calls came in while running, schedule them with proper debounce delay
            if (pendingArgs !== null) {
                timeoutId = setTimeout(() => {
                    timeoutId = null;
                    runFunc();
                }, wait);
            }
        };

        const debounced = ((...args: Parameters<T>) => {
            pendingArgs = args;

            // Clear existing timeout
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }

            // If currently running, the function will reschedule itself when done
            if (isRunning) {
                return;
            }

            // Schedule new execution
            timeoutId = setTimeout(() => {
                timeoutId = null;
                runFunc();
            }, wait);
        }) as T & { cancel: () => void };

        // Add cancel method for cleanup
        (debounced as T & { cancel: () => void }).cancel = () => {
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            pendingArgs = null;
        };

        return debounced;
    }

    static throttle<T extends (...args: unknown[]) => Promise<void> | void>(
        func: T,
        wait: number
    ): T & { cancel: () => void } {
        let lastCallTime = 0;
        let isRunning = false;
        let latestArgs: Parameters<T> | null = null;
        let timeoutId: ReturnType<typeof setTimeout> | null = null;

        const runFunc = async () => {
            if (!latestArgs) return;

            isRunning = true;
            const args = latestArgs;
            latestArgs = null;

            try {
                await func(...args);
            } finally {
                isRunning = false;
                lastCallTime = Date.now(); // Move here - track completion time
            }

            // Schedule next execution if there are pending calls
            if (latestArgs) {
                timeoutId = setTimeout(() => {
                    runFunc();
                }, wait);
            }
        };

        const throttled = ((...args: Parameters<T>) => {
            latestArgs = args;

            if (isRunning || timeoutId !== null) {
                return; // Already scheduled or running
            }

            const timeSinceLastCall = Date.now() - lastCallTime;

            if (timeSinceLastCall >= wait) {
                runFunc();
            } else {
                timeoutId = setTimeout(() => {
                    timeoutId = null;
                    runFunc();
                }, wait - timeSinceLastCall);
            }
        }) as T & { cancel: () => void };

        (throttled as T & { cancel: () => void }).cancel = () => {
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
                timeoutId = null;
            }
            latestArgs = null;
        };

        return throttled;
    }

    static capitalizeFirstLetter(s: string) {
        return s.charAt(0).toUpperCase() + s.slice(1);
    }

    static getDecimal(num: number): number {
        return num > 0 ? num % 1 : -num % 1;
    }

    static deepCopy(obj: unknown): unknown {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        let copy: any;

        if (null == obj || 'object' !== typeof obj) return obj;

        if (obj instanceof Date) {
            copy = new Date();
            copy.setTime(obj.getTime());
            return copy;
        }

        if (obj instanceof Array) {
            copy = [];
            for (let i = 0; i < obj.length; i++) {
                copy[i] = Extensions.deepCopy(obj[i]);
            }
            return copy;
        }

        if (obj instanceof Object) {
            copy = {};
            for (const attr in obj) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any, no-prototype-builtins
                if (obj.hasOwnProperty(attr)) copy[attr] = Extensions.deepCopy((obj as any)[attr]);
            }
            return copy;
        }

        return {};
    }
}
